import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Form, Field, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { AccountContext } from '../../components/AccountContext';
import { NotificationContext } from '../../components/GrowlNotifications';
import PageNotFound from '../../components/PageNotFound';
import { Loading } from '../../components/Loading';
import { validationMessages, notificationMessages } from '../../i18n/common';
import { InputTextWithValidationMessage, DropdownWithValidation } from '../../components/prime-formik';
import { useSelector } from 'react-redux';
import { AccountType } from '../../shared/Constants';
import { buildVatIdExampleText } from '../../utils/AppUtil';
import UpdateCompanyDataConfirmationDialog from './UpdateCompanyDataConfirmationDialog';
import { parse } from 'query-string';

const messages = defineMessages({
  legalName: { id: 'attrs.legalName', defaultMessage: 'Legal Name' },
  displayName: { id: 'attrs.displayName', defaultMessage: 'Display Name' },
  addressLine1: { id: 'attrs.addresLine1', defaultMessage: 'Address Line 1' },
  addressLine2: { id: 'attrs.addresLine2', defaultMessage: 'Address Line 2' },
  city: { id: 'attrs.city', defaultMessage: 'City' },
  state: { id: 'attrs.state', defaultMessage: 'State (optional)' },
  country: { id: 'attrs.country', defaultMessage: 'Country' },
  postalCode: { id: 'attrs.postalCode', defaultMessage: 'Postal Code' },
  vatCountry: { id: 'attrs.vatCountry', defaultMessage: 'VAT/GST country' },
  vatId: { id: 'attrs.vatId', defaultMessage: 'VAT/GST ID' },
  vatIdPlaceholder: { id: 'attrs.vatIdPlaceholder', defaultMessage: 'Example: {vatIdExample}' },
  registrationNumber: { id: 'attrs.regNo', defaultMessage: 'Registration Number' },
  dpoEmail: { id: 'attrs.dpo', defaultMessage: 'Data Protection Officer (optional)' },
  creditNotificationThreshold: {
    id: 'attrs.creditNotificationThreshold',
    defaultMessage: 'Credit Notification Threshold',
  },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  updateCompanyData: {
    id: 'accounts.update-invoice-address-and-other-company-data.heading',
    defaultMessage: 'Update Invoice Address and other Company Data',
  },
  vatIdNotValid: {
    id: 'attrs.vatIdNotValid',
    defaultMessage: 'VAT ID is not valid',
  },
});

const UPDATE_COMPANY_DATA = gql`
  mutation updateCompanyInformation($input: UpdateCompanyInformationInput!) {
    updateCompanyInformation(input: $input) {
      account {
        id
        companyInformation {
          name
          displayName
          addressLine1
          addressLine2
          city
          state
          country
          postalCode
          taxData {
            id
            type
            country
            description
            taxId
          }
          totalNumberEmployees
          registrationNumber
          dpoEmail
          creditNotificationThreshold
        }
      }
    }
  }
`;

const ACCOUNT_QUERY = gql`
  query querySingleAccountById($accountId: ID!) {
    account(id: $accountId) {
      id
      type
      companyInformation {
        name
        displayName
        addressLine1
        addressLine2
        city
        state
        country
        postalCode
        taxData {
          id
          type
          country
          description
          taxId
        }
        totalNumberEmployees
        registrationNumber
        dpoEmail
        creditNotificationThreshold
      }
    }

    countries: availableCountries

    availableTaxIds {
      id
      type
      country
      description
      variants {
        regularExpression
        example
      }
    }
  }
`;

const UpdateCompanyInformation = ({ toggleEdit }) => {
  const [selectedTaxId, setSelectedTaxId] = useState(null);
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);

  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const location = useLocation();

  const params = parse(location.search);
  const focusOrganisationSize = params.focusOrganisationSize || false;

  const {
    cldr: { teritoryDisplayNames },
  } = useSelector((state) => state.i18n);

  const { loading, error, data } = useQuery(ACCOUNT_QUERY, {
    variables: {
      accountId: currentAccount,
    },
  });

  useEffect(() => {
    const accountHasTaxInformation = data && data.account && data.account.companyInformation.taxData;

    if (accountHasTaxInformation) {
      const availableTaxIds = data.availableTaxIds;

      const accountTaxInformation = data.account.companyInformation.taxData;

      const taxInformation = availableTaxIds.find(
        (availableTaxId) =>
          availableTaxId.type === accountTaxInformation.type &&
          availableTaxId.country === accountTaxInformation.country &&
          availableTaxId.description === accountTaxInformation.description,
      );

      if (taxInformation) {
        setSelectedTaxId(taxInformation);
      }
    }
  }, [data]);

  const inputComponent = document.querySelector('#organisationSize');

  useEffect(() => {
    if (!!focusOrganisationSize && !!inputComponent) {
      inputComponent.focus();
    }
  }, [inputComponent, focusOrganisationSize]);

  const [updateCompanyInformationMutation] = useMutation(UPDATE_COMPANY_DATA, {
    refetchQueries: ['querySingleAccountById', 'getViewerAccounts'],
  });

  if (error) {
    growl.show({ severity: 'error', detail: error.message, sticky: true });
  }

  if (loading) {
    return <Loading />;
  }

  if (!data) {
    return <PageNotFound />;
  }

  const {
    account: { type },
    countries,
    availableTaxIds,
  } = data;

  const countryOptions = teritoryDisplayNames(countries);
  const taxIdOptions = availableTaxIds.map((taxId) => ({
    value: taxId.id,
    label: `${taxId.country} - ${taxId.description}`,
  }));

  const accountIsNewBusinessModel =
    type === AccountType.GSC || type === AccountType.GP || type === AccountType.NBM_CLIENT;
  const accountCanBeAStripeCustomer = accountIsNewBusinessModel || type === AccountType.MCP;

  const taxIdValidationSchema = (() => {
    if (accountCanBeAStripeCustomer) {
      if (selectedTaxId) {
        return Yup.string()
          .required(formatMessage(validationMessages.required))
          .matches(new RegExp(selectedTaxId.regularExpression), formatMessage(messages.vatIdNotValid))
          .nullable();
      } else {
        return Yup.string()
          .required(formatMessage(validationMessages.required))
          .nullable();
      }
    } else {
      if (selectedTaxId) {
        return Yup.string()
          .matches(new RegExp(selectedTaxId.regularExpression), formatMessage(messages.vatIdNotValid))
          .nullable();
      } else {
        return Yup.string().nullable();
      }
    }
  })();

  const validationSchema = Yup.object().shape({
    account: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    legalName: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    displayName: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    addressLine1: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    addressLine2: Yup.string().default(''),
    city: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    country: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    postalCode: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    taxIdType: Yup.string().when([], {
      is: () => accountCanBeAStripeCustomer,
      then: Yup.string()
        .required(formatMessage(validationMessages.required))
        .nullable(),
      otherwise: Yup.string().nullable(),
    }),
    taxId: taxIdValidationSchema,
    registrationNumber: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    organisationSize: Yup.number().required(formatMessage(validationMessages.required)),
    creditNotificationThreshold: Yup.number()
      .required(formatMessage(validationMessages.required))
      .default(1),
  });

  const initialValues = {
    ...validationSchema.cast(),
    account: currentAccount,
    legalName: data.account.companyInformation.name,
    displayName: data.account.companyInformation.displayName,
    addressLine1: data.account.companyInformation.addressLine1,
    addressLine2: data.account.companyInformation.addressLine2,
    city: data.account.companyInformation.city,
    state: data.account.companyInformation.state,
    country: data.account.companyInformation.country,
    postalCode: data.account.companyInformation.postalCode,
    taxIdType: data.account.companyInformation.taxData ? data.account.companyInformation.taxData.id : null,
    taxId: data.account.companyInformation.taxData ? data.account.companyInformation.taxData.taxId : null,
    registrationNumber: data.account.companyInformation.registrationNumber,
    dpoEmail: data.account.companyInformation.dpoEmail,
    organisationSize: data.account.companyInformation.totalNumberEmployees,
    creditNotificationThreshold: data.account.companyInformation.creditNotificationThreshold,
  };

  const handleSubmit = async (values) => {
    const {
      account,
      legalName,
      displayName,
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      postalCode,
      taxIdType,
      taxId,
      registrationNumber,
      dpoEmail,
      organisationSize,
      creditNotificationThreshold,
    } = values;

    try {
      await updateCompanyInformationMutation({
        variables: {
          input: {
            account,
            organisationSize,
            companyInformation: {
              legalName,
              displayName,
              addressLine1,
              addressLine2,
              city,
              state,
              country,
              postalCode,
              taxIdType,
              taxId,
              registrationNumber,
              dpoEmail,
              creditNotificationThreshold,
            },
          },
        },
      });
      growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success) });
      toggleEdit('companyData');
    } catch (error) {
      growl.show({ severity: 'error', detail: error.message, sticky: true });
    }
  };

  const vatIdPlaceholderExample = buildVatIdExampleText(selectedTaxId);

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <Panel header={formatMessage(messages.updateCompanyData)}>
          <div className="p-grid">
            <div className="p-sm-12 p-md-6">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  handleSubmit(values);
                }}
              >
                {({ setFieldValue }) => {
                  return (
                    <Form>
                      <UpdateCompanyDataConfirmationDialog
                        confirmationModalVisible={confirmationModalVisible}
                        hide={() => setConfirmationModalVisible(false)}
                      />
                      <input type="hidden" name="account" value={currentAccount} />
                      <div className="p-grid p-fluid">
                        <div className="p-col-12">
                          <Field
                            id="legalName"
                            name="legalName"
                            type="text"
                            labelText={formatMessage(messages.legalName)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="displayName"
                            name="displayName"
                            type="text"
                            labelText={formatMessage(messages.displayName)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="addressLine1"
                            name="addressLine1"
                            type="text"
                            labelText={formatMessage(messages.addressLine1)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="addressLine2"
                            name="addressLine2"
                            type="text"
                            labelText={formatMessage(messages.addressLine2)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="city"
                            name="city"
                            type="text"
                            labelText={formatMessage(messages.city)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="state"
                            name="state"
                            type="text"
                            labelText={formatMessage(messages.state)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="country"
                            name="country"
                            labelText={formatMessage(messages.country)}
                            component={DropdownWithValidation}
                            filter
                            filterBy="label,value"
                            options={countryOptions}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="postalCode"
                            name="postalCode"
                            type="text"
                            labelText={formatMessage(messages.postalCode)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <label className="field-label" htmlFor="taxIdType">
                            <FormattedMessage id="attrs.vatCountry" defaultMessage="VAT/GST country" />
                          </label>
                          <Field
                            id="taxIdType"
                            name="taxIdType"
                            labelText={formatMessage(messages.vatCountry)}
                            filter
                            filterBy="label,value"
                            options={taxIdOptions}
                            component={DropdownWithValidation}
                            onChange={(e) => {
                              setFieldValue('taxIdType', e.target.value);

                              const taxInformation = availableTaxIds.find(
                                (availableTaxId) => availableTaxId.id === e.target.value,
                              );

                              if (taxInformation) {
                                setSelectedTaxId(taxInformation);
                              }
                            }}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            name="taxId"
                            labelText={formatMessage(messages.vatId)}
                            placeholderText={formatMessage(messages.vatIdPlaceholder, {
                              vatIdExample: vatIdPlaceholderExample,
                            })}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="registrationNumber"
                            name="registrationNumber"
                            type="text"
                            labelText={formatMessage(messages.registrationNumber)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="dpoEmail"
                            name="dpoEmail"
                            type="text"
                            labelText={formatMessage(messages.dpoEmail)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="organisationSize"
                            name="organisationSize"
                            type="number"
                            min="1"
                            labelText="Organisation size"
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="creditNotificationThreshold"
                            name="creditNotificationThreshold"
                            type="number"
                            min="1"
                            labelText={formatMessage(messages.creditNotificationThreshold)}
                            component={InputTextWithValidationMessage}
                          />
                        </div>
                      </div>
                      <div className="p-grid">
                        <div className="p-col-12">
                          <Button
                            type="button"
                            className="p-button-secondary"
                            label={formatMessage(messages.cancel)}
                            onClick={toggleEdit}
                          />
                          {accountCanBeAStripeCustomer ? (
                            <Button
                              type="button"
                              onClick={() => setConfirmationModalVisible(true)}
                              label={formatMessage(messages.submit)}
                            />
                          ) : (
                            <Button type="submit" label={formatMessage(messages.submit)} />
                          )}
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
};

UpdateCompanyInformation.propTypes = {
  toggleEdit: PropTypes.func.isRequired,
};

export default UpdateCompanyInformation;
